import { useAuth0 } from '@auth0/auth0-react';
import type { RegisteredRouter } from '@tanstack/react-router';
import { Link, useMatch } from '@tanstack/react-router';
import type { RoutePaths } from '@tanstack/router-core';
import { useEffect } from 'react';
import type { Store } from '../../data/store';
import { isDemoStore } from '../../data/store';
import { isReceivingEnabled } from '../../helpers/feature-flags';
import { useIsAdmin } from '../../hooks/admin-access';
import { useCurrentStore } from '../../router';
import LogoutButton from '../logout-button';
import { NavWrapper } from './nav-wrapper';
import { StoreMenu } from './store-menu';

type ValidRoutePath = Exclude<RoutePaths<RegisteredRouter['routeTree']>, `/${string}/`>;

const baseClassName = 'rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200';
const mobileBaseClassName = 'block rounded-md px-3 py-2 font-medium transition-colors duration-200';

interface AppNavItem {
  label: string;
  to: ValidRoutePath;
  end?: boolean;
  inactiveClassName?: string;
  activeClassName?: string;
  target?: '_blank';
  rel?: string;
}

const buildAppNavItems = (isAdmin: boolean, store: Store): AppNavItem[] => {
  const items: AppNavItem[] = [{ label: 'Dashboard', to: '/stores/$storeId', end: true }];

  if (isReceivingEnabled(store)) {
    items.push({ label: 'Receiving', to: '/stores/$storeId/receiving' });
  }

  items.push(
    { label: 'Orders', to: '/stores/$storeId/orders' },
    { label: 'Products', to: '/stores/$storeId/products' },
    { label: 'Reports', to: '/stores/$storeId/reports' },
    { label: 'Vendors', to: '/stores/$storeId/vendors' },
    { label: 'Uploads', to: '/stores/$storeId/uploads' }
  );

  if (isAdmin) {
    // Admin opens in a new tab so the original tab keeps its store context —
    // Dirk/Maxime hop into admin while still browsing a customer's store.
    items.push({
      label: 'Admin',
      to: '/admin',
      target: '_blank',
      rel: 'noopener noreferrer',
      inactiveClassName: 'text-white bg-orange-600 hover:bg-orange-700',
      activeClassName: 'text-white bg-orange-800',
    });
  }

  return items;
};

const AppNavDesktopItem: React.FC<{ item: AppNavItem }> = ({ item }) => {
  const inactiveClass =
    item.inactiveClassName || 'text-gray-300 hover:bg-primary-800 hover:text-white';
  const activeClass = item.activeClassName || 'bg-primary-700 text-white';

  return (
    <Link
      to={item.to}
      params={(prev) => prev}
      target={item.target}
      rel={item.rel}
      activeOptions={{ exact: item.end }}
      activeProps={{ className: `${baseClassName} ${activeClass}` }}
      inactiveProps={{ className: `${baseClassName} ${inactiveClass}` }}
    >
      {item.label}
    </Link>
  );
};

const AppNavMobileItem: React.FC<{ item: AppNavItem; onClick: () => void }> = ({
  item,
  onClick,
}) => {
  const inactiveClass = item.inactiveClassName || 'text-gray-300';
  const activeClass = item.activeClassName || 'bg-primary-700 text-white';

  return (
    <Link
      to={item.to}
      params={(prev) => prev}
      target={item.target}
      rel={item.rel}
      activeOptions={{ exact: item.end }}
      activeProps={{ className: `${mobileBaseClassName} ${activeClass}` }}
      inactiveProps={{ className: `${mobileBaseClassName} ${inactiveClass}` }}
      onClick={onClick}
    >
      {item.label}
    </Link>
  );
};

export const AppNavBar = () => {
  const { isAuthenticated } = useAuth0();
  const onStoreRoute = useMatch({ from: '/authenticated/stores/$storeId', shouldThrow: false });
  const isAdmin = useIsAdmin();
  const store = useCurrentStore();
  const isDemo = isDemoStore(store);

  // Sync demo theme class on SPA navigation (initial paint handled by inline script in index.html).
  // Only toggle once the store is loaded — removing the class while store is undefined would
  // flash emerald over the amber theme the inline script already applied.
  useEffect(() => {
    if (store) {
      document.documentElement.classList.toggle('demo-theme', isDemo);
    }
  }, [store, isDemo]);

  if (!store) {
    return (
      <NavWrapper
        items={[]}
        DesktopItemRenderer={AppNavDesktopItem}
        MobileItemRenderer={AppNavMobileItem}
        RightNav={isAuthenticated && !onStoreRoute ? <LogoutButton /> : null}
      />
    );
  }

  return (
    <NavWrapper
      items={buildAppNavItems(isAdmin, store)}
      DesktopItemRenderer={AppNavDesktopItem}
      MobileItemRenderer={AppNavMobileItem}
      RightNav={<StoreMenu store={store} />}
      isDemo={isDemo}
    />
  );
};
